// src/lib/api.ts
import { decodeBase64urlToString } from "./base64url";
import { joinUrl, stripTrailingSlash } from "./strings";

const RAW_API = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
const API = stripTrailingSlash(RAW_API);

function parseJwt(token: string | null | undefined) {
  try {
    const t = (token ?? "").toString();
    const parts = t.split(".");
    if (parts.length < 2 || !parts[1]) return null;

    const jsonStr = decodeBase64urlToString(parts[1]);
    if (!jsonStr) return null;

    return JSON.parse(decodeURIComponent(escape(jsonStr)));
  } catch {
    return null;
  }
}

function isTokenValid(token: string | null) {
  const payload = parseJwt(token);
  if (!payload?.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now + 5;
}

export async function api(
  path: string,
  opts: RequestInit = {},
  needsAuth = false
) {
  const headers = new Headers(opts.headers || {});
  headers.set(
    "Content-Type",
    headers.get("Content-Type") || "application/json"
  );

  if (needsAuth) {
    const token = localStorage.getItem("admin_token");
    if (token && isTokenValid(token)) {
      headers.set("Authorization", `Bearer ${token}`);
    } else {
      localStorage.removeItem("admin_token");
      window.location.href = "/admin/login";
      throw new Error(token ? "Session expired" : "No token");
    }
  }

  const url = joinUrl(API, path);
  const res = await fetch(url, { ...opts, headers });

  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem("admin_token");
    window.location.href = "/admin/login";
    throw new Error("Unauthorized");
  }

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    /*
     * This used to throw the parsed body as-is: a plain `{ error: "..." }`
     * object with no `message` property. Every caller reports failures with
     * `e?.message ?? "<generic>"`, so the server's actual explanation ("game
     * code already exists", "invalid code (2-40 chars: letters, numbers,
     * . _ -)") was dropped and the user only ever saw "Create failed".
     */
    const detail =
      (data && (data.error || data.message)) ||
      res.statusText ||
      `Request failed (${res.status})`;

    const err = new Error(String(detail)) as Error & {
      status?: number;
      data?: unknown;
    };
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}
